/**
 * MobileMoreDrawer — slide-up drawer containing all secondary mobile nav links
 * Extracted from MobileNav so it can be triggered from the top bar "More" button.
 */
import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Camera, Mic, CheckSquare, ScanLine, User, ChevronRight, HelpCircle, type LucideIcon } from 'lucide-react'
import { tokens } from '@/design-system/tokens'
import { useProject } from '@/contexts/ProjectContext'
import { apiGet, signOut } from '@/lib/api'
import type { ApiProfile } from '@/lib/mobile-api-types'

// Phase C §07.2 — quick capture entries.
// Photo targets the Daily Log surface, pre-filled with the active project when
// one is selected, and triggers the device camera via the `intent=camera` query
// param. Falls back to the global /m/reports list when no project is active so
// the entry point still works from outside a project context.
interface QuickCaptureEntry {
  key: string
  icon: LucideIcon
  label: string
  buildPath: (projectId: string | number | null | undefined) => string
}

const QUICK_CAPTURE: readonly QuickCaptureEntry[] = [
  {
    key: 'photo',
    icon: Camera,
    label: 'Photo to daily log',
    buildPath: pid => (pid ? `/m/p/${pid}/log?intent=camera` : '/m/reports?intent=camera'),
  },
  {
    key: 'voice',
    icon: Mic,
    label: 'Voice note to daily log',
    buildPath: pid => (pid ? `/m/p/${pid}/log?intent=voice` : '/m/reports?intent=voice'),
  },
  {
    key: 'task',
    icon: CheckSquare,
    label: 'Add task',
    buildPath: () => '/m/tasks/new',
  },
  {
    key: 'scan-bill',
    icon: ScanLine,
    label: 'Scan bill',
    buildPath: () => '/m/bills/new?scan=1',
  },
]

// Items in MORE_SECTIONS support either a static `path` or a `buildPath`
// function that receives the active project ID — same pattern as QUICK_CAPTURE.
// Finish Schedule is project-aware: with a project selected, navigate directly
// to that project's FS; otherwise fall back to the all-projects rollup.
// Bug #55: /m/finish-schedule was hardcoded, ignoring the selected project.
// Help item: isHelp: true triggers the onHelpClick callback instead of navigation.
interface MoreSectionItem {
  label: string
  path?: string
  buildPath?: (pid: string | number | null | undefined) => string
  isHelp?: boolean
}

const MORE_SECTIONS: { title: string; items: MoreSectionItem[] }[] = [
  {
    title: 'Financial',
    items: [
      { path: '/m/invoices', label: 'Invoices' },
      { path: '/m/estimates', label: 'Estimates' },
      { path: '/m/proposals', label: 'Proposals' },
      { path: '/m/change-orders', label: 'Change Orders' },
      { path: '/m/purchase-orders', label: 'Purchase Orders' },
      { path: '/m/bills', label: 'Bills' },
    ],
  },
  {
    title: 'Project',
    items: [
      {
        // Project Logs is project-scoped — MobileProjectLogPage lives at
        // /m/p/:id/log. Route to the active project's log list; fall back to
        // the daily-reports page (which has a project picker) when no project
        // is selected. Same project-aware pattern as Finish Schedule below.
        // Bug #1274: was hardcoded to /m/reports (the daily-reports voice
        // page), never reaching the project-logs list.
        label: 'Project Logs',
        buildPath: pid => (pid ? `/m/p/${pid}/log` : '/m/reports'),
      },
      { path: '/m/rfis', label: 'RFIs' },
      { path: '/m/submittals', label: 'Submittals' },
      { path: '/m/procurement', label: 'Procurement' },
      { path: '/m/files', label: 'Files' },
      { path: '/m/my-schedule', label: 'My Schedule' },
      { path: '/m/schedule', label: 'Project Tasks' },
      {
        label: 'Finish Schedule',
        buildPath: pid => pid ? `/m/p/${pid}/fs` : '/m/finish-schedule',
      },
      { path: '/m/timesheets', label: 'Timesheets' },
      { path: '/m/equipment', label: 'Equipment' },
      { path: '/m/locations', label: 'Locations' },
    ],
  },
  {
    title: 'Settings & Team',
    items: [
      { path: '/m/settings', label: 'Settings' },
      { path: '/m/settings/company-settings', label: 'Company Settings' },
      { path: '/m/contacts', label: 'Contacts' },
      { label: 'Help', isHelp: true },
    ],
  },
]

interface MobileMoreDrawerProps {
  open: boolean
  onClose: () => void
  onHelpClick?: () => void
}

export function MobileMoreDrawer({ open, onClose, onHelpClick }: MobileMoreDrawerProps) {
  const navigate = useNavigate()
  const { activeProject } = useProject()
  const [profile, setProfile] = useState<ApiProfile | null>(null)

  useEffect(() => {
    apiGet('/profile').then((p: any) => setProfile(p as ApiProfile)).catch(() => {})
  }, [])

  const go = (path: string) => { navigate(path); onClose() }

  return (
    <>
      {/* Backdrop — transparent, catches outside clicks */}
      {open && (
        <div onClick={onClose} style={{ position: 'fixed', inset: 0, zIndex: 203 }} />
      )}

      {/* Dropdown — top-right per spec §03 */}
      <div style={{
        position: 'fixed',
        top: 54,
        right: 14,
        width: 266,
        background: 'var(--bg-surface)',
        border: '1px solid var(--border)',
        borderRadius: 12,
        padding: 6,
        boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
        zIndex: 204,
        maxHeight: 'calc(100dvh - 54px - 78px - env(safe-area-inset-bottom))',
        overflowY: 'auto',
        transformOrigin: 'top right',
        transform: open ? 'scale(1)' : 'scale(0.92)',
        opacity: open ? 1 : 0,
        pointerEvents: open ? 'auto' : 'none',
        transition: 'transform 0.18s cubic-bezier(0.34, 1.56, 0.64, 1), opacity 0.14s ease',
      }}>
        {/* Account — user identity row at top, taps to /m/profile */}
        <SheetLabel>Account</SheetLabel>
        <button
          onClick={() => go('/m/profile')}
          style={{
            display: 'flex', alignItems: 'center', gap: 10,
            padding: '8px 12px', borderRadius: 6,
            width: '100%', background: 'none', border: 'none',
            cursor: 'pointer', textAlign: 'left',
          }}
        >
          <span style={{
            width: 32, height: 32, display: 'grid', placeItems: 'center',
            borderRadius: '50%', flexShrink: 0,
            background: 'var(--bg-elevated)',
            color: 'var(--text-secondary)',
          }}>
            <User size={15} />
          </span>
          <span style={{ flex: 1, minWidth: 0 }}>
            <span style={{
              display: 'block',
              font: '600 13px/1.2 ' + tokens.typography.fontFamily.sans,
              color: 'var(--text-primary)',
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}>
              {profile?.name || 'My Profile'}
            </span>
            {profile?.email && (
              <span style={{
                display: 'block',
                font: '400 11px/1.2 ' + tokens.typography.fontFamily.sans,
                color: 'var(--text-muted)',
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              }}>
                {profile.email}
              </span>
            )}
          </span>
          <ChevronRight size={14} color="var(--text-muted)" style={{ flexShrink: 0 }} />
        </button>

        <SheetDivider />

        {/* Quick capture */}
        <SheetLabel>Quick capture</SheetLabel>
        {QUICK_CAPTURE.map(qc => (
          <SheetRow
            key={qc.key}
            icon={<qc.icon size={14} />}
            label={qc.label}
            highlight
            onClick={() => go(qc.buildPath(activeProject?.id ?? null))}
          />
        ))}

        <SheetDivider />

        {/* Jump to */}
        <SheetLabel>Jump to</SheetLabel>
        <SheetRow label="Bills"        onClick={() => go('/m/bills')} />
        <SheetRow label="Timesheets"  onClick={() => go('/m/timesheets')} />
        <SheetRow label="My Schedule" onClick={() => go('/m/my-schedule')} />

        <SheetDivider />

        {MORE_SECTIONS.map(section => (
          <React.Fragment key={section.title}>
            <SheetLabel>{section.title}</SheetLabel>
            {section.items.map(item => {
              if (item.isHelp) {
                return (
                  <SheetRow
                    key="help"
                    icon={<HelpCircle size={14} />}
                    label={item.label}
                    onClick={() => {
                      onClose()
                      onHelpClick?.()
                    }}
                  />
                )
              }
              const itemPath = item.buildPath
                ? item.buildPath(activeProject?.id ?? null)
                : (item.path ?? '/')
              return (
                <SheetRow
                  key={item.path ?? item.label}
                  label={item.label}
                  onClick={() => go(itemPath)}
                />
              )
            })}
            <SheetDivider />
          </React.Fragment>
        ))}

        {/* Log out */}
        <button
          onClick={async () => {
            await signOut()
            onClose()
            navigate('/login')
          }}
          style={{
            display: 'flex', alignItems: 'center', gap: 11,
            padding: '9px 12px', borderRadius: 6,
            width: '100%', background: 'none', border: 'none',
            color: 'var(--st-error)',
            font: '600 13px/1.1 ' + tokens.typography.fontFamily.sans,
            cursor: 'pointer', fontFamily: 'inherit', textAlign: 'left',
          }}
        >
          Log Out
        </button>
      </div>
    </>
  )
}

function SheetLabel({ children }: { children: React.ReactNode }) {
  return (
    <div style={{
      padding: '9px 12px 6px',
      font: '600 9.5px/1 ' + tokens.typography.fontFamily.mono,
      letterSpacing: '.16em', color: 'var(--text-muted)', textTransform: 'uppercase',
    }}>
      {children}
    </div>
  )
}

function SheetDivider() {
  return <hr style={{ border: 'none', borderTop: '1px solid var(--border)', margin: '6px 8px' }} />
}

function SheetRow({ icon, label, highlight, onClick }: {
  icon?: React.ReactNode; label: string; highlight?: boolean; onClick: () => void
}) {
  return (
    <button
      onClick={onClick}
      style={{
        display: 'flex', alignItems: 'center', gap: 11,
        padding: '9px 12px', borderRadius: 6,
        width: '100%', background: 'none', border: 'none',
        color: 'var(--text-primary)',
        font: '500 13px/1.1 ' + tokens.typography.fontFamily.sans,
        cursor: 'pointer', fontFamily: 'inherit', textAlign: 'left',
      }}
    >
      {icon && (
        <span style={{
          width: 26, height: 26, display: 'grid', placeItems: 'center',
          borderRadius: 5, flexShrink: 0,
          background: highlight ? 'color-mix(in oklch, var(--brand-gold) 12%, transparent)' : 'var(--bg-elevated)',
          color: highlight ? 'var(--brand-gold)' : 'var(--text-secondary)',
        }}>
          {icon}
        </span>
      )}
      <span style={{ flex: 1 }}>{label}</span>
    </button>
  )
}
